import { CalculationInput, CalculationResult } from "@/types";
import { MapData } from "@/features/maps/core/types/MapData";
import {
  calculateZodiacSign,
  calculateGematria,
  calculateNumerology,
  calculateProfile,
  SIGNS_MAP,
  calculateTur,
} from "@/utils";
import { parseDateString } from "@/utils/dates";
import { logger } from "@/lib/logger";
import { CalculationResultSchema } from "@/schemas/mapData";
import { createMapFromCalculation } from "@/features/maps/utils/mapMapper";

/**
 * Perform a full calculation based on input data
 */
export function calculateMap(input: CalculationInput): CalculationResult {
  // Basic validation to trigger fatal error if input is corrupt (like in tests)
  if (!input || typeof input.birthDate !== "string") {
    // This will throw if birthDate is a getter designed to throw
  }

  try {
    // 1. Calculate Zodiac (Inner Sign) with Saved DB Override Awareness
    let innerSign = { sign: "?", number: 0, age: 0 };
    try {
      if (input.savedInnerSign?.number && input.savedInnerSign.number > 0) {
        const calculated = calculateZodiacSign(
          input.birthDate,
          input.birthTime,
          input.timezone,
          input.sunLongitude
        );
        innerSign = {
          number: input.savedInnerSign.number,
          sign: input.savedInnerSign.sign || SIGNS_MAP[input.savedInnerSign.number] || "?",
          age: calculated.age
        };
      } else {
        innerSign = calculateZodiacSign(
          input.birthDate,
          input.birthTime,
          input.timezone,
          input.sunLongitude
        );
      }
    } catch (e) {
      logger.warn("Zodiac calculation failed", e, {
        context: "calculateMap.zodiac",
        input,
      });
    }

    // 2. Calculate Gematria (Surrounding Sign) with Saved DB Override Awareness
    let surroundingSign = { sign: "?", number: 0, gematria: 0 };
    try {
      if (input.savedSurroundingSign?.number && input.savedSurroundingSign.number > 0) {
        const nameForGematria = `${input.firstName} ${input.motherName}`.trim();
        const calculatedGematria = nameForGematria ? calculateGematria(nameForGematria).gematria : 0;
        surroundingSign = {
          number: input.savedSurroundingSign.number,
          sign: input.savedSurroundingSign.sign || SIGNS_MAP[input.savedSurroundingSign.number] || "?",
          gematria: input.savedSurroundingSign.gematria !== undefined ? input.savedSurroundingSign.gematria : calculatedGematria
        };
      } else {
        const nameForGematria = `${input.firstName} ${input.motherName}`.trim();
        if (nameForGematria) {
          surroundingSign = calculateGematria(nameForGematria);
        }
      }
    } catch (e) {
      logger.warn("Gematria calculation failed", e, {
        context: "calculateMap.gematria",
        input,
      });
    }

    // 3. Calculate Numerology
    let numerology: CalculationResult["numerology"] = {
      dayNumber: 0,
      monthNumber: 0,
      yearNumber: 0,
      totalNumber: 0,
      totalFromAllDigits: 0,
      firstNumber: 0,
      familyNumber: 0,
      totalNameNumber: 0,
      eheviNumber: 0,
      itzurimNumber: 0,
      eheviInnerSumFirst: 0,
      eheviInnerSumFamily: 0,
      itzurimInnerSumFirst: 0,
      itzurimInnerSumFamily: 0,
      fullInnerSum: 0,
      eheviInnerSum: 0,
      itzurimInnerSum: 0,
      full2InnerSum: 0,
      hamashaOuterSum: 0,
      hamashaInnerSum: 0,
    };

    try {
      numerology = calculateNumerology(input);
    } catch (e) {
      logger.warn("Numerology calculation failed", e, {
        context: "calculateMap.numerology",
        input,
      });
    }

    // 4. Calculate Profile with Saved DB Override Awareness
    let profile = { profileNumber: 0, profileSign: "?", zodiacPosition: 0 };
    try {
      if (input.savedProfile?.profileNumber && input.savedProfile.profileNumber > 0) {
        const rawProfileNum = input.savedProfile.profileNumber;
        const reducedZodiac = ((rawProfileNum - 1) % 12) + 1;
        profile = {
          profileNumber: rawProfileNum,
          profileSign: input.savedProfile.profileSign || SIGNS_MAP[reducedZodiac] || "?",
          zodiacPosition: reducedZodiac
        };
      } else {
        profile = calculateProfile(innerSign.number, surroundingSign.number);
      }
    } catch (e) {
      logger.warn("Profile calculation failed", e, {
        context: "calculateMap.profile",
      });
    }

    // 5. Calculate Tur with Saved DB Override Awareness
    let tur = { value: "-", label: "" };
    try {
      if (input.savedTur?.value && input.savedTur.value !== "-") {
        tur = {
          value: input.savedTur.value,
          label: input.savedTur.label || ""
        };
      } else {
        tur = calculateTur(innerSign.number, surroundingSign.number);
      }
    } catch (e) {
      logger.warn("Tur calculation failed", e, { context: "calculateMap.tur" });
    }

    // 6. Compute Esoteric Completeness Flags
    const missingFields: string[] = [];
    if (!input.firstName) missingFields.push("firstName");
    if (!input.familyName) missingFields.push("familyName");
    if (!input.motherName) missingFields.push("motherName");
    if (!input.birthDate) missingFields.push("birthDate");
    
    const isTimeFallback = !input.birthTime || input.birthTime === "00:00" || input.birthTime === "00:00:00" || input.birthTime.startsWith("00:00");
    if (isTimeFallback) missingFields.push("birthTime");
    
    if (input.lat === undefined || input.lat === null) missingFields.push("lat");
    if (input.lng === undefined || input.lng === null) missingFields.push("lng");

    const completenessFlags = {
      canCalculateHouses: !!(
        input.birthTime &&
        !isTimeFallback &&
        input.lat !== undefined &&
        input.lat !== null &&
        input.lng !== undefined &&
        input.lng !== null
      ),
      isMoonVolatile: isTimeFallback,
      hasAstrology: !!input.birthDate,
      hasNumerology: !!(input.birthDate && input.firstName && input.familyName),
      hasShabtai: !!(input.firstName && input.familyName && input.motherName),
      missingFields,
    };

    if (process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.log("[SRL CALCULATOR DEBUG] Input Birth Details:", {
        firstName: input.firstName,
        birthDate: input.birthDate,
        birthTime: input.birthTime,
        isTimeFallback,
        lat: input.lat,
        lng: input.lng
      });
      // eslint-disable-next-line no-console
      console.log("[SRL CALCULATOR DEBUG] Computed Completeness Flags:", completenessFlags);
    }

    return {
      innerSign,
      surroundingSign,
      profile,
      numerology,
      tur,
      completenessFlags,
    };
  } catch (error) {
    logger.error("Fatal calculation error", error, {
      context: "calculateMap.fatal",
      input,
    });
    throw error;
  }
}

/**
 * Calculate from raw fields (useful for Excel import or form data)
 */
export function calculateFromFields(
  firstName: string,
  familyName: string,
  motherName: string,
  birthDate: string, // DD/MM/YYYY
): { input: CalculationInput; result: CalculationResult } {
  const input: CalculationInput = {
    firstName,
    familyName,
    motherName,
    birthDate,
  };

  return {
    input,
    result: calculateMap(input),
  };
}

/**
 * Recalculate from existing MapData (useful for inline editing)
 */
export function recalculateMap(map: MapData): MapData {
  const input: CalculationInput = {
    firstName: map.firstName || "",
    familyName: map.familyName || "",
    motherName: map.motherName || "",
    birthDate: "",
    birthTime: map.birth_time || undefined,
    cityName: map.birth_location || undefined,
    lat: map.birth_lat || undefined,
    lng: map.birth_lon || undefined,
    timezone: map.timezone || undefined,
  };

  if (map.birthDate) {
    const parsed = parseDateString(map.birthDate);
    if (parsed) {
      input.birthDate = parsed;
    }
  }

  const result = calculateMap(input);

  let innerSignOverridden = false;
  let surroundingSignOverridden = false;

  if (
    map.inner_sign_number &&
    map.inner_sign_number !== result.innerSign.number
  ) {
    result.innerSign.number = map.inner_sign_number;
    result.innerSign.sign = SIGNS_MAP[map.inner_sign_number] || "?";
    innerSignOverridden = true;
  }

  if (
    map.surrounding_sign_number &&
    map.surrounding_sign_number !== result.surroundingSign.number
  ) {
    result.surroundingSign.number = map.surrounding_sign_number;
    result.surroundingSign.sign = SIGNS_MAP[map.surrounding_sign_number] || "?";
    surroundingSignOverridden = true;
  }

  if (innerSignOverridden || surroundingSignOverridden) {
    try {
      result.tur = calculateTur(
        result.innerSign.number,
        result.surroundingSign.number,
      );
      result.profile = calculateProfile(
        result.innerSign.number,
        result.surroundingSign.number,
      );
    } catch (e) {
      logger.warn("Recalculation failed after override", e, {
        context: "recalculateMap.override",
      });
    }
  }

  // Now map the final result
  const finalMap = createMapFromCalculation({
    userId: map.user_id,
    input: {
      ...input,
      birthDate: map.birthDate || input.birthDate,
    },
    result,
    notes: map.notes,
    labels: map.labels || [],
    id: map.id,
    createdAt: map.created_at || new Date().toISOString(),
  });

  // Preserve the existing astrology cache so it is not lost upon unrelated edits
  finalMap.astrology_cache = map.astrology_cache;
  finalMap.gender = map.gender;
  finalMap.soul_root = map.soul_root;

  return finalMap;
}

/**
 * Validate calculation results using Zod schemas
 */
export function validateCalculation(result: CalculationResult): {
  valid: boolean;
  errors: string[];
} {
  try {
    const validation = CalculationResultSchema.safeParse(result);

    if (!validation.success) {
      const errors = validation.error.issues.map(
        (i) => `${i.path.join(".")}: ${i.message}`,
      );
      return { valid: false, errors };
    }

    return { valid: true, errors: [] };
  } catch (error) {
    logger.error("Validation failed unexpectedly", error, {
      context: "validateCalculation",
    });
    return { valid: false, errors: ["Validation failed unexpectedly"] };
  }
}
